/* eslint no-shadow: 0 */
import { createSelector } from 'reselect';
import sortBy from 'lodash/sortBy';
import numeral from 'numeral';
import differenceInCalendarYears from 'date-fns/difference_in_calendar_years';
import differenceInCalendarMonths from 'date-fns/difference_in_calendar_months';
import differenceInCalendarDays from 'date-fns/difference_in_calendar_days';
import differenceInHours from 'date-fns/difference_in_hours';
import endOfMonth from 'date-fns/end_of_month';

//constants
import { languages, pollutantsInfo, timezone, dictionary } from './constants';

//default values

import { defaultProduct, defaultComparingProduct } from './constants';


const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const loading = ({ forecastDemo }) => forecastDemo.products.loading;
const error = ({ forecastDemo }) => forecastDemo.products.error;
const products = ({ forecastDemo }) => forecastDemo.products.data;
const productId = ({ forecastDemo }) => forecastDemo.state.productId;
const pollutant = ({ forecastDemo }) => forecastDemo.state.pollutant;
const member = ({ forecastDemo }) => forecastDemo.state.member;
const year = ({ forecastDemo }) => forecastDemo.state.year;
const month = ({ forecastDemo }) => forecastDemo.state.month;
const day = ({ forecastDemo }) => forecastDemo.state.day;
const hour = ({ forecastDemo }) => forecastDemo.state.hour;
const comparingYear = ({ forecastDemo }) => forecastDemo.state.comparingYear;
const comparingMonth = ({ forecastDemo }) => forecastDemo.state.comparingMonth;
const comparingDay = ({ forecastDemo }) => forecastDemo.state.comparingDay;
const comparingHour = ({ forecastDemo }) => forecastDemo.state.comparingHour;
const basemap = ({ forecastDemo }) => forecastDemo.state.basemap;
const labels = ({ forecastDemo }) => forecastDemo.state.labels;
const language = ({ forecastDemo }) => forecastDemo.state.language;
const timezones = ({ forecastDemo }) => forecastDemo.state.timezone;
const viewport = ({ forecastDemo }) => forecastDemo.state.viewport;

/**
 * Get the valid products for the forecast demo
 */
export const getValidProducts = createSelector([loading, error, products], (
  loading,
  error,
  products,
) => {
  if (loading || error) return [];

  //important!! TO DO - adjust filters properly, no data from API yet
  return products.filter(
    product => product.productType.code.toLowerCase() === 'std_res_forecast'
  );
});

/**
 * Get the selected product
 */
export const getSelectedProduct = createSelector(
  [getValidProducts, productId],
  (products, productId) => {
    if (products.length && productId) {
      return products.find(product => product.id === productId);
    }
    return null;
  },
);

/**
 * Get the selected product
 */
export const getSelectedComparingProduct = createSelector(
  [getValidProducts],
  (products) => {
    if (products.length && defaultComparingProduct) {
      return products.find(product => product.id === defaultComparingProduct);
    }
    return null;
  },
);



/**
 * Get the area options
 */
export const getAreas = createSelector(
  [getValidProducts],
  products => sortBy(
    products.map(product => ({
      label: product.domain.name,
      value: product.domain.code,
    })),
    ['label'],
  ),
);

/**
 * Get the selected area option
 */
export const getSelectedArea = createSelector(
  [getAreas, getSelectedProduct],
  (areas, product) => {
    if (!product) return null;
    return areas.find(area => area.value === product.domain.code);
  },
);

/**
 * Get the pollutants options
 */
export const getPollutants = createSelector([getSelectedProduct], (product) => {
  if (!product) return [];
  return sortBy(
    product.variables.map((pollutant) => {
      const label = pollutantsInfo[pollutant]
        ? pollutantsInfo[pollutant].displayName
        : pollutant;
      const description = pollutantsInfo[pollutant]
        ? pollutantsInfo[pollutant].description
        : pollutant;
      return { label, value: pollutant, description };
    }),
    ['label'],
  );
});

/**
 * Get the selected pollutant option
 */
export const getSelectedPollutant = createSelector(
  [getPollutants, pollutant],
  (pollutants, pollutant) =>
    // The second part of the condition is used to validate
    // a restored state&& pollutants.find(p => p.value !== )

    (pollutant && pollutants.find(p => p.value === pollutant.value)
      ? {
        label: pollutant.label,
        value: pollutant.value,
        description: pollutant.description,
      }
      : null),
);


/**
 * Get the members options
 */
export const getMembers = createSelector([getSelectedProduct], (product) => {
  if (!product) return [];
  if (!product.ensembleMembers) return null;

  const members = product.ensembleMembers
  return sortBy(
    members.map(member => ({
      label: member.replace('KNMI:CHIMERE-2013', 'Cimere (KNMI)'),
      value: member,
    })),
    ['label'],
  );
});

/**
 * Get the selected member option
 */
export const getSelectedMember = createSelector([getMembers, member], (
  members,
  member,
) => {
  const option = member && members && members.find(m => m.value === member);
  // The second part of the condition is used to validate
  // a restored state
  return member && members && option
    ? { label: option.label, value: member }
    : null;
});

/**
 * Get the selected language option
 */
export const getSelectedLanguageF = createSelector(
  [language],
  language => languages.find(l => l.value === language),
);

/**
 * Get the selected timezone option
 */
export const getSelectedTimezone = createSelector(
  [timezones],
  timezones => timezone.find(t => t.value === timezones),
);

/**
 * Get the months options
 */
export const getMonths = createSelector([getSelectedProduct], (product) => {
  if (!product) return [];

  const range = [new Date(product.tMax), new Date(product.tMin)];

  // This condition can occur when restoring a previous state
  if (range[0] < range[1]) return [];

  const diff = differenceInCalendarMonths(...range);
  const firstMonth = new Date(product.tMin).getUTCMonth();


  return new Array(diff + 1)
    .fill(null)
    .map((_, i) => ({ label: MONTHS[firstMonth + i], value: firstMonth + i }));
});

/**
 * Get the selected month option
 */
export const getSelectedMonth = createSelector(
  [getMonths, month],
  (months, month) => // The second part of the condition is used to validate
    // a restored state
    (month !== null && months.find(m => m.value === month)
      ? { label: MONTHS[month], value: month }
      : null),
);

/**
 * Get the years options
 */
export const getYears = createSelector([getSelectedProduct], (product) => {
  if (!product) return [];

  const range = [new Date(product.tMax), new Date(product.tMin)];
  const diff = differenceInCalendarYears(...range);
  const startYear = range[1].getUTCFullYear();
  return new Array(diff + 1)
    .fill(null)
    .map((_, i) => ({ label: `${startYear + i}`, value: startYear + i }));
});

/**
 * Get the selected year option
 */
export const getSelectedYear = createSelector(
  [getYears, year],
  (years, year) => // The second part of the condition is used to validate
    // a restored state
    (year && years.find(y => y.value === year)
      ? { label: `${year}`, value: year }
      : null),
);
/**
 * Get the days options
 */
export const getDays = createSelector(
  [getSelectedProduct, getSelectedMonth],
  (product, month) => {
    if (!product || !month) return [];

    const firstYear = new Date(product.tMax).getUTCFullYear();
    const lastMonth = new Date(product.tMax).getUTCMonth();
    const lastDay = new Date(product.tMax).getUTCDate();
    const firstDay = new Date(product.tMin).getUTCDate();

    let range;
    if (month.value === lastMonth) {
      range = [
        new Date(Date.UTC(firstYear, lastMonth, lastDay)),
        new Date(Date.UTC(firstYear, month.value, firstDay)),
      ];
    } else {
      range = [
        endOfMonth(new Date(Date.UTC(firstYear, month.value, firstDay))),
        new Date(Date.UTC(firstYear, month.value, firstDay)),
      ];
    }

    // This condition can occur when restoring a previous state
    if (month.value < lastMonth) return [];

    const diff = differenceInCalendarDays(...range);

    return new Array(diff + 1)
      .fill(null)
      .map((_, i) => ({ label: `${firstDay + i}`, value: firstDay + i }));
  },
);

/**
 * Get the selected day option
 */
export const getSelectedDay = createSelector(
  [getDays, day],
  (days, day) => // The second part of the condition is used to validate
    // a restored state
    (day !== null && days.find(d => d.value === day)
      ? { label: `${day}`, value: day }
      : null),
);

/**
 * Get the hour options
 */
export const getHours = createSelector(
  [getSelectedProduct, getSelectedMonth, getSelectedDay],
  (product, month, day) => {
    if (!product || !month || !day) return [];

    const year = new Date(product.tMax).getUTCFullYear();
    const firstDay = new Date(product.tMin).getUTCDate();
    const firstHour = new Date(product.tMin).getUTCHours();
    const lastMonth = new Date(product.tMax).getUTCMonth();
    const lastDay = new Date(product.tMax).getUTCDate();
    const lastHour = new Date(product.tMax).getUTCHours();

    let range;
    if (month.value === lastMonth && day.value === lastDay) {
      range = [
        new Date(Date.UTC(year, lastMonth, lastDay, lastHour)),
        new Date(
          Date.UTC(
            year,
            month.value,
            lastDay,
            firstDay === lastDay ? firstHour : 0,
          ),
        ),
      ];
    } else {
      range = [
        new Date(Date.UTC(year, month.value, day.value, 23)),
        new Date(
          Date.UTC(
            year,
            month.value,
            day.value,
            firstDay === day.value ? firstHour : 0,
          ),
        ),
      ];
    }

    const diff = differenceInHours(...range);
    const startHour = range[1].getUTCHours();

    return new Array(diff + 1)
      .fill(null)
      .map((_, i) => ({
        label: `${startHour + i < 10 ? '0' : ''}${startHour + i}:00`,
        value: startHour + i,
      }));
  },
);

/**
 * Get the selected hour option
 */
export const getSelectedHour = createSelector(
  [getHours, hour],
  (hours, hour) =>  // The second part of the condition is used to validate
    // a restored state
    (hour !== null && hours.find(h => h.value === hour)
      ? { label: `${hour < 10 ? '0' : ''}${hour}:00`, value: hour }
      : null),
);

/**
 * Get the comparing years options
 */
export const getComparingYears = createSelector(
  [getSelectedComparingProduct, getSelectedYear, getSelectedMonth],
  (product, year, month) => {
    if (!product || !year || !month) return [];

    const range = [
      new Date(product.tMax),
      new Date(Date.UTC(year.value, month.value, 1)),
    ];

    // This condition can occur when restoring a previous state
    if (range[0] < range[1]) return [];

    const diff = differenceInCalendarYears(...range);
    const startYear = range[1].getUTCFullYear();

    return new Array(diff + 1)
      .fill(null)
      .map((_, i) => ({ label: `${startYear + i}`, value: startYear + i }));
  },
);

/**
 * Get the selected comparing year option
 */
export const getSelectedComparingYear = createSelector(
  [getComparingYears, comparingYear],
  (years, year) =>  // The second part of the condition is used to validate
    // a restored state
    (year && years.find(y => y.value === year)
      ? { label: `${year}`, value: year }
      : null),
);

export const getComparingMonths = createSelector(
  [getSelectedComparingProduct, getSelectedMonth, getSelectedDay],
  (product, month, day) => {
    if (!product || !month || !day) return [];
    const year = new Date(product.tMin).getUTCFullYear();

    const range = [
      new Date(product.tMax),
      new Date(Date.UTC(year, month.value, day.value)),
    ];

    // This condition can occur when restoring a previous state
    if (range[0] < range[1]) return [];

    const diff = differenceInCalendarMonths(...range);

    return new Array(diff + 1)
      .fill(null)
      .map((_, i) => ({
        label: MONTHS[month.value + i],
        value: month.value + i,
      }));
  },
);

/**
 * Get the selected comparing month option
 */
export const getSelectedComparingMonth = createSelector(
  [getComparingMonths, comparingMonth],
  (months, month) =>  // The second part of the condition is used to validate
    // a restored state
    (month !== null && months.find(m => m.value === month)
      ? { label: MONTHS[month], value: month }
      : null),
);

/**
 * Get the comparing days options
 */
export const getComparingDays = createSelector(
  [
    getSelectedComparingProduct,
    getSelectedMonth,
    getSelectedComparingMonth,
    getSelectedDay,
  ],
  (product, month, comparingMonth, day) => {
    if (!product || !month || !comparingMonth || !day) return [];

    const year = new Date(product.tMax).getUTCFullYear();
    const lastMonth = new Date(product.tMax).getUTCMonth();
    const lastDay = new Date(product.tMax).getUTCDate();

    // const firstDay = month.value === comparingMonth.value ? day.value : 1;
    const firstDay = new Date(product.tMin).getUTCDate();
    const isLastDay = comparingMonth.value === lastMonth
      && day.value === lastDay;
    if (isLastDay) {
      return [{ label: day.label, value: day.value }];
    }


    let range;
    // The selected comparing month is the same month as the one of the
    // available date range
    if (lastMonth === comparingMonth.value) {
      range = [
        new Date(product.tMax),
        new Date(Date.UTC(year, comparingMonth.value, firstDay)),
      ];
    } else {
      range = [
        endOfMonth(Date.UTC(year, comparingMonth.value, firstDay)),
        new Date(Date.UTC(year, comparingMonth.value, firstDay)),
      ];
    }
    //  let range;

    // The selected comparing month is the same month as the one of the
    // available date range
    if (month.value === lastMonth) {
      range = [
        new Date(Date.UTC(year, lastMonth, lastDay)),
        new Date(Date.UTC(year, month.value, firstDay)),
      ];
    } else {
      range = [
        endOfMonth(new Date(Date.UTC(year, month.value, firstDay))),
        new Date(Date.UTC(year, month.value, firstDay)),
      ];
    }

    // This condition can occur when restoring a previous state
    if (month.value < lastMonth) return [];

    const diff = differenceInCalendarDays(...range);

    return new Array(diff + 1)
      .fill(null)
      .map((_, i) => ({ label: `${firstDay + i}`, value: firstDay + i }));
  },
);

/**
 * Get the selected comparing day option
 */
export const getSelectedComparingDay = createSelector(
  [getComparingDays, comparingDay],
  (days, day) => // The second part of the condition is used to validate
    // a restored state
    (day !== null && days.find(d => d.value === day)
      ? { label: `${day}`, value: day }
      : null),
);

/**
 * Get the comparing hours options
 */
export const getComparingHours = createSelector(
  [
    getSelectedProduct,
    getSelectedComparingMonth,
    getSelectedComparingDay,
    getSelectedMonth,
    getSelectedDay,
    getSelectedHour,
  ],
  (product, comparingMonth, comparingDay, month, day, hour) => {
    if (!product || !comparingMonth || !comparingDay || !month || !day || !hour) return [];

    const year = new Date(product.tMax).getUTCFullYear();
    const lastMonth = new Date(product.tMax).getUTCMonth();
    const lastDay = new Date(product.tMax).getUTCDate();
    const lastHour = new Date(product.tMax).getUTCHours();

    const isLastDay = comparingMonth.value === lastMonth
      && comparingDay.value === lastDay;
    const isLastHourLastDay = isLastDay
      && month.value === lastMonth
      && day.value === lastDay
      && hour.value === lastHour;
    const isLastHourOfDay = hour.value === 23;

    // if (isLastHourLastDay || isLastHourOfDay) {
    //   return [];
    // }

    const initialHour = month.value === comparingMonth.value
      && day.value === comparingDay.value
      ? hour.value + 1
      : 0;

    let range;
    if (isLastDay) {
      range = [
        new Date(Date.UTC(year, lastMonth, lastDay, lastHour)),
        new Date(Date.UTC(year, lastMonth, lastDay, initialHour)),
      ];
    } else {
      range = [
        new Date(Date.UTC(year, comparingMonth.value, comparingDay.value, 23)),
        new Date(
          Date.UTC(year, comparingMonth.value, comparingDay.value, initialHour),
        ),
      ];
    }

    const diff = differenceInHours(...range);
    const startHour = range[1].getUTCHours();

    return new Array(diff + 1)
      .fill(null)
      .map((_, i) => ({
        label: `${startHour + i < 10 ? '0' : ''}${startHour + i}:00`,
        value: startHour + i,
      }));
  },
);

/**
 * Get the selected comparing hour option
 */
export const getSelectedComparingHour = createSelector(
  [getComparingHours, comparingHour],
  (hours, hour) => // The second part of the condition is used to validate
    // a restored state
    (hour !== null && hours.find(h => h.value === hour)
      ? { label: `${hour < 10 ? '0' : ''}${hour}:00`, value: hour }
      : null)
  ,
);

/**
 * Return the bounds of the map
 */
export const getBounds = createSelector([getSelectedArea, getSelectedProduct], (area, product) => {
  if (!area) return null;
  if (dictionary[area.label] === product.domain.code) {
    const coords = dictionary[area.label];
    return [
      [coords.latMax, coords.lonMin],
      [coords.latMin, coords.lonMax]
    ];
  }
  if (area.value === product.domain.code) {
    return [
      [product.latMax, product.lonMin],
      [product.latMin, product.lonMax]
    ];
  }
});


/**
 * Get the URL of the tiles
 */
export const getTilesUrl = createSelector(
  [
    products,
    getSelectedPollutant,
    getMembers,
    getSelectedMember,
    getSelectedYear,
    getSelectedMonth,
    getSelectedDay,
    getSelectedHour,
  ],
  (demoProducts, pollutant, members, member, year, month, day, hour) => {
    if (
      (!members || !pollutant
        || members) && (!member
          || !year
          || !month
          || !day
          || !hour)
    ) return null;
    const product = demoProducts.find(p => p.id === defaultProduct); // Limited just for demo

    try {
      const date = new Date(product.tMax);
      date.setUTCFullYear(year.value);
      date.setUTCMonth(month.value);
      date.setUTCDate(day.value);
      date.setUTCHours(hour.value);
      date.setUTCMinutes(0);
      date.setUTCSeconds(0);
      const retina = window.devicePixelRatio > 1 ? '@2x' : '';
      const ensembleMember = member ? `&ensembleMember=${member.value}` : '';
      return `${process.env.REACT_APP_API_URL}/product/${product.id}/tile/{z}/{x}/{y}${retina}.png?variable=${pollutant.value}${ensembleMember}&t=${date.toISOString()}`;
    } catch (err) {
      // We can enter this case if the date was restored from the URL
      return null;
    }
  },
);

/**
 * Get the URL of the "comparing" tiles
 */
export const getComparingTilesUrl = createSelector(
  [
    products,
    getSelectedPollutant,
    getMembers,
    getSelectedMember,
    getSelectedComparingYear,
    getSelectedComparingMonth,
    getSelectedComparingDay,
    getSelectedComparingHour,
  ],
  (demoProducts, pollutant, members, member, year, month, day, hour) => {
    if (
      (!demoProducts
        || !pollutant
        || members) && (!member
          || !year
          || !month
          || !day
          || !hour)
    ) return null;

    const product = demoProducts.find(p => p.id === defaultComparingProduct); // Limited just to that product
    try {
      const date = new Date(product.tMax);
      date.setUTCFullYear(year.value);
      date.setUTCMonth(month.value);
      date.setUTCDate(day.value + 1);
      date.setUTCHours(hour.value);
      date.setUTCMinutes(0);
      date.setUTCSeconds(0);

      const retina = window.devicePixelRatio > 1 ? '@2x' : '';
      const ensembleMember = member ? `&ensembleMember=${member.value}` : '';
      return `${process.env.REACT_APP_API_URL}/product/${product.id}/tile/{z}/{x}/{y}${retina}.png?variable=${pollutant.value}${ensembleMember}&t=${date.toISOString()}`;
    } catch (err) {
      // We can enter this case if the date was restored from the URL
      return null;
    }
  },
);

const legendItems = [
  { color: 'rgb(0, 0, 143)' },
  { color: 'rgb(0, 0, 255)' },
  { color: 'rgb(0, 127, 255)' },
  { color: 'rgb(0, 255, 255)' },
  { color: 'rgb(127, 255, 127)' },
  { color: 'rgb(255, 255, 0)' },
  { color: 'rgb(255, 127, 0)' },
  { color: 'rgb(255, 0, 0)' },
  { color: 'rgb(127, 0, 0)' },
];

/**
 * Get legend values
 */
export const getLegendValues = createSelector(
  [getSelectedProduct, getSelectedPollutant],
  (product, pollutant) => {
    if (!product || !pollutant) return [];

    const legendValue = product.defaultRenderProps[`${pollutant.value}`];
    const unit = legendValue.units;
    const { length } = legendItems;
    const res = legendItems.map((item, index) => {
      let value = (legendValue.valueMin + legendValue.valueMax)
        / length
        * (index + 1);
      if (value >= 1000) {
        value = numeral(Math.floor(value)).format('0.0a');
      } else if (value < 10) {
        value = value.toFixed(1);
      } else {
        value = Math.round(value);
      }
      return { ...item, name: `${pollutant.value}_${index}`, value, unit };
    });
    return res;
  },
);

/**
 * Get the selected basemap ID
 */
export const getSelectedBasemap = createSelector(
  [basemap],
  basemap => basemap,
);

/**
 * Get the selected labels ID
 * NOTE: the id can evaluate to "none" meaning no
 * labels are displayed
 */
export const getSelectedLabels = createSelector([labels], labels => labels);

export const getViewport = createSelector([viewport], viewport => viewport);



// WEBPACK FOOTER //
// ./src/components/forecast-demo/selectors.js